"use client";

import { useState } from "react";
import { track } from "@/lib/analytics/mixpanel";

type Status = "idle" | "loading" | "success" | "error";

export default function NewsletterForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const email = (
      e.currentTarget.elements.namedItem("email") as HTMLInputElement
    ).value;

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (!res.ok) {
        setErrorMsg(json.error ?? "Something went wrong. Please try again.");
        setStatus("error");
        track("Newsletter Signup Failed", { status: res.status });
      } else {
        setStatus("success");
        e.currentTarget.reset();
        track("Newsletter Signup Submitted");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
      track("Newsletter Signup Failed", { reason: "network" });
    }
  }

  if (status === "success") {
    return (
      <p className="text-sm font-medium text-brand-purple-light">
        You&apos;re subscribed. Welcome aboard!
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <form
        className="flex gap-2"
        onSubmit={handleSubmit}
        aria-label="Newsletter signup"
      >
        {/* Honeypot */}
        <input
          type="text"
          name="_hp"
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute h-0 w-0 overflow-hidden opacity-0"
        />
        <label htmlFor="newsletter-email" className="sr-only">
          Email address
        </label>
        <input
          id="newsletter-email"
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          suppressHydrationWarning
          placeholder="Your email"
          required
          disabled={status === "loading"}
          aria-invalid={status === "error"}
          aria-describedby={status === "error" ? "newsletter-error" : undefined}
          className="flex-1 min-w-0 rounded-full border border-ink/[0.12] bg-white/5 px-4 py-2.5 text-base sm:text-sm text-white placeholder:text-neutral-500 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/40 disabled:opacity-50 min-h-[44px]"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          aria-label="Subscribe to newsletter"
          className="shrink-0 rounded-full bg-brand-purple px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-purple-light disabled:cursor-not-allowed disabled:opacity-60 min-h-[44px]"
        >
          {status === "loading" ? "…" : "Subscribe"}
        </button>
      </form>
      {status === "error" && (
        <p id="newsletter-error" role="alert" className="pl-1 text-xs text-red-400">
          {errorMsg}
        </p>
      )}
    </div>
  );
}
